import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    addPolarisSupportTicketMessage,
    addSupportTicketMessage,
    closeSupportTicket,
    formatSupportTicketDate,
    formatSupportTicketStatus,
    SupportTicketDetail,
    SupportTicketMessage,
    SupportTicketSeverity,
    SupportTicketStatus,
    updatePolarisSupportTicket,
    updateSupportTicket,
} from "../services/supportTicketsService";
import SupportTicketSelect from "./SupportTicketSelect";
import "../styles/feature-requests.css";
import "../styles/support-tickets.css";

const TITLE_MAX = 500;
const MESSAGE_MAX = 1000;

const SEVERITY_OPTIONS = [
    { value: "HIGH", label: "High" },
    { value: "MEDIUM", label: "Medium" },
    { value: "LOW", label: "Low" },
];

const STATUS_OPTIONS = [
    { value: "RAISED", label: "Raised" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "CLOSED", label: "Closed" },
];

type Props = {
    open: boolean;
    ticket: SupportTicketDetail | null;
    onClose: () => void;
    onUpdated: (ticket: SupportTicketDetail) => void;
    /** When true, use Polaris admin API and allow full field edits for non-closed tickets. */
    polarisAdmin?: boolean;
};

export default function SupportTicketDetailModal({
    open,
    ticket,
    onClose,
    onUpdated,
    polarisAdmin = false,
}: Props) {
    const titleId = useId();
    const replyInputRef = useRef<HTMLTextAreaElement>(null);
    const [title, setTitle] = useState("");
    const [severity, setSeverity] = useState<SupportTicketSeverity>("MEDIUM");
    const [status, setStatus] = useState<SupportTicketStatus>("RAISED");
    const [messages, setMessages] = useState<SupportTicketMessage[]>([]);
    const [canReply, setCanReply] = useState(false);
    const [replyBody, setReplyBody] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [replyError, setReplyError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [closing, setClosing] = useState(false);
    const [replying, setReplying] = useState(false);

    useEffect(() => {
        if (!open || !ticket) return;
        setTitle(ticket.title);
        setSeverity(ticket.severity);
        setStatus(ticket.status);
        setMessages(ticket.messages ?? []);
        setCanReply(ticket.canReply ?? false);
        setReplyBody("");
        setError(null);
        setReplyError(null);
        setSaving(false);
        setClosing(false);
        setReplying(false);
    }, [open, ticket]);

    const resizeReplyInput = useCallback(() => {
        const el = replyInputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 280)}px`;
    }, []);

    useEffect(() => {
        if (!open) return;
        resizeReplyInput();
    }, [replyBody, open, resizeReplyInput]);

    if (!open || !ticket) return null;

    const canEdit = ticket.canEdit;
    const canClose = ticket.canClose;
    const canSetInProgress = polarisAdmin || canEdit;

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canEdit) return;
        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            setError("Title is required.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                title: trimmedTitle,
                severity,
                status,
            };
            const updated = polarisAdmin
                ? await updatePolarisSupportTicket(ticket.id, payload)
                : await updateSupportTicket(ticket.id, payload);
            onUpdated(updated);
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to update ticket.");
        } finally {
            setSaving(false);
        }
    };

    const handleCloseTicket = async () => {
        if (!canClose) return;
        setClosing(true);
        setError(null);
        try {
            if (polarisAdmin) {
                const updated = await updatePolarisSupportTicket(ticket.id, {
                    title: title.trim() || ticket.title,
                    severity,
                    status: "CLOSED",
                });
                onUpdated(updated);
            } else {
                const updated = await closeSupportTicket(ticket.id);
                onUpdated(updated);
            }
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to close ticket.");
        } finally {
            setClosing(false);
        }
    };

    const handleSendReply = async () => {
        if (!canReply) return;
        const trimmed = replyBody.trim();
        if (!trimmed) {
            setReplyError("Please enter a message.");
            return;
        }
        if (trimmed.length > MESSAGE_MAX) {
            setReplyError("Message must be 1,000 characters or fewer.");
            return;
        }
        setReplying(true);
        setReplyError(null);
        try {
            const created = polarisAdmin
                ? await addPolarisSupportTicketMessage(ticket.id, trimmed)
                : await addSupportTicketMessage(ticket.id, trimmed);
            setMessages((prev) => [...prev, created]);
            setReplyBody("");
            resizeReplyInput();
            if (polarisAdmin && ticket.status === "RAISED") {
                onUpdated({
                    ...ticket,
                    status: "IN_PROGRESS",
                    messages: [...messages, created],
                    canReply: true,
                });
            }
        } catch (err: unknown) {
            setReplyError(err instanceof Error ? err.message : "Failed to send message.");
        } finally {
            setReplying(false);
        }
    };

    return createPortal(
        <div className="feature-idea-modal-backdrop">
            <div
                className="feature-idea-modal support-ticket-modal support-ticket-detail-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${titleId}-heading`}
                data-testid="support-ticket-detail-modal"
            >
                <div className="feature-idea-modal-header">
                    <button
                        type="button"
                        className="feature-idea-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                    <h2 id={`${titleId}-heading`}>Ticket {ticket.ticketNumber}</h2>
                    <p>
                        Raised {formatSupportTicketDate(ticket.createdAt)} by{" "}
                        {[ticket.raisedByFirstName, ticket.raisedByLastName].filter(Boolean).join(" ") || "—"}
                        {ticket.companyName ? ` · ${ticket.companyName}` : ""}
                    </p>
                </div>

                <div className="feature-idea-modal-body">
                    <div className="feature-idea-field">
                        <label htmlFor={`${titleId}-title`}>Title</label>
                        <input
                            id={`${titleId}-title`}
                            type="text"
                            value={title}
                            maxLength={TITLE_MAX}
                            disabled={!canEdit}
                            data-testid="support-ticket-detail-title"
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>

                    <div className="feature-idea-field">
                        <label htmlFor={`${titleId}-description`}>Description</label>
                        <textarea
                            id={`${titleId}-description`}
                            value={ticket.description}
                            readOnly
                            disabled
                            data-testid="support-ticket-detail-description"
                        />
                    </div>

                    <div className="support-ticket-conversation" data-testid="support-ticket-conversation">
                        <h3 className="support-ticket-conversation-heading">Conversation</h3>
                        {messages.length === 0 ? (
                            <p className="support-ticket-conversation-empty">No replies yet.</p>
                        ) : (
                            <div className="support-ticket-message-list">
                                {messages.map((message) => (
                                    <div
                                        key={message.id}
                                        className="support-ticket-message"
                                        data-testid={`support-ticket-message-${message.id}`}
                                    >
                                        <div className="support-ticket-message-meta">
                                            <strong>{message.authorUsername}</strong>
                                            <span className="support-ticket-message-meta-sep">·</span>
                                            <span>{formatSupportTicketDate(message.createdAt)}</span>
                                        </div>
                                        <p className="support-ticket-message-body">{message.body}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {canReply && (
                            <div className="support-ticket-reply-form" data-testid="support-ticket-reply-form">
                                <textarea
                                    ref={replyInputRef}
                                    value={replyBody}
                                    maxLength={MESSAGE_MAX}
                                    placeholder="Write a reply…"
                                    aria-label="Reply"
                                    data-testid="support-ticket-reply-input"
                                    onChange={(e) => {
                                        setReplyBody(e.target.value);
                                        if (replyError) setReplyError(null);
                                        resizeReplyInput();
                                    }}
                                />
                                <button
                                    type="button"
                                    className="feature-requests-pill-btn"
                                    disabled={replying || saving || closing}
                                    data-testid="support-ticket-reply-button"
                                    onClick={() => void handleSendReply()}
                                >
                                    {replying ? "Sending…" : "Send reply"}
                                </button>
                                {replyError && (
                                    <div className="feature-idea-field-error" role="alert">
                                        {replyError}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <form onSubmit={handleSave} noValidate>
                        <div className="support-ticket-detail-row">
                        <div className="feature-idea-field">
                            <label htmlFor={`${titleId}-severity`}>Severity</label>
                            <SupportTicketSelect
                                id={`${titleId}-severity`}
                                value={severity}
                                options={SEVERITY_OPTIONS}
                                disabled={!canEdit}
                                data-testid="support-ticket-detail-severity"
                                onChange={(next) => setSeverity(next as SupportTicketSeverity)}
                            />
                        </div>
                        <div className="feature-idea-field">
                            <label htmlFor={`${titleId}-status`}>Status</label>
                            {canEdit && canSetInProgress ? (
                                <SupportTicketSelect
                                    id={`${titleId}-status`}
                                    value={status}
                                    options={STATUS_OPTIONS}
                                    data-testid="support-ticket-detail-status"
                                    onChange={(next) => setStatus(next as SupportTicketStatus)}
                                />
                            ) : (
                                <input
                                    id={`${titleId}-status`}
                                    type="text"
                                    value={formatSupportTicketStatus(ticket.status)}
                                    disabled
                                    data-testid="support-ticket-detail-status"
                                />
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="feature-idea-field-error" role="alert" style={{ marginBottom: "0.75rem" }}>
                            {error}
                        </div>
                    )}

                    <div className="feature-idea-modal-actions support-ticket-detail-actions">
                        <button
                            type="button"
                            className="support-ticket-close-btn"
                            disabled={closing || saving}
                            data-testid="support-ticket-detail-cancel"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        {canClose && (
                            <button
                                type="button"
                                className="support-ticket-close-btn"
                                disabled={closing || saving}
                                data-testid="support-ticket-close-button"
                                onClick={() => void handleCloseTicket()}
                            >
                                {closing ? "Closing…" : "Close ticket"}
                            </button>
                        )}
                        {canEdit && (
                            <button
                                type="submit"
                                className="feature-idea-modal-submit is-valid"
                                disabled={saving || closing}
                                data-testid="support-ticket-save-button"
                            >
                                {saving ? "Saving…" : "Save changes"}
                            </button>
                        )}
                    </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body,
    );
}
